import { CloseIcon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  Checkbox,
  Grid,
  Input,
  Link,
  Text,
  Tooltip
} from "@chakra-ui/react";
import apiCarteira from "api/api-carteira";
import { getErrorPadrao } from "components/atendimentos-components/atendimento-form/functions/mapeamentos";
import { CustomModal } from "components/custom-modal";
import { Toast } from "components/toast";
import { CashIcon } from "components/vectors/cash-icon";
import { getTitle } from "components/white-label";
import { useState } from "react";
import { makeEvent, useEventListener } from "services/events";
import { currencyMaskFormat } from "utils/string-formats";

const initialValue = {
  tipoCarteira: "GERAL",
  value: 0
};

const STORM_URL = "https://sistema.novafinanceira.com/";

export function ModalAdicionarSaldoStorm({
  updateTransacoes,
  saldoAtual
}: {
  updateTransacoes?: () => Promise<void>;
  saldoAtual?: number;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [modalData, setModalData] = useState(initialValue);
  const [isLoading, setIsLoading] = useState(false);
  const [estouCiente, setEstouCiente] = useState(false);
  const title = getTitle() === "Nova Promotora" ? "Grupo Nova" : getTitle();
  const modalTitle = "Adicione Saldo via C/C " + title;

  const onOpen = () => {
    setIsOpen(true);
  };

  const onClose = () => {
    setIsOpen(false);
    setModalData(initialValue);
    setIsLoading(false);
    setEstouCiente(false);
    updateTransacoes?.();
  };

  useEventListener("openModalAdicionarSaldoStorm", onOpen);

  const isDisabledDepositar =
    !modalData.value || modalData.value < 20 || !estouCiente;

  const handleSubmit = async () => {
    if (!isDisabledDepositar) {
      setIsLoading(true);
      try {
        await apiCarteira.post(`/cobrancas/cc-storm`, modalData);
        onClose();
      } catch (e: any) {
        const { msgInterface } = getErrorPadrao(e);
        Toast({
          title: "Erro ao realizar deposito",
          description: msgInterface,
          status: "error",
          duration: 10000
        });
      } finally {
        setIsLoading(false);
      }
    }
  };

  const tooltipLabel = !modalData.value || modalData.value < 20
    ? "Preencha o valor a ser depositado"
    : !estouCiente
    ? "Marque a opção \"Estou ciente\" para continuar"
    : undefined;

  const modalFooter = (
    <>
      <Tooltip label={tooltipLabel}>
        <Button
          onClick={handleSubmit}
          opacity={isDisabledDepositar ? 0.5 : undefined}
          leftIcon={<CashIcon width="18px" height="18px" />}
          isLoading={isLoading}
        >
          Depositar
        </Button>
      </Tooltip>
      <Button
        onClick={onClose}
        variant="outline"
        leftIcon={<CloseIcon w="12px" h="12px" />}
      >
        Fechar
      </Button>
    </>
  );

  return (
    <CustomModal
      isOpen={isOpen}
      onClose={onClose}
      modalTitle={modalTitle}
      size="xl"
      scroll="inside"
      modalFooter={modalFooter}
      isLoading={isLoading}
      containerProps={{ alignItems: "flex-start", py: "40px" }}
    >
      <Grid w="100%" gap="16px" pb="4px">
        <Box>
          <Text>
            Carteira:{" "}
            <Text as="span" fontWeight="semibold">
              GERAL
            </Text>
          </Text>
        </Box>

        <Box>
          <Text mb="8px">Valor (mín. R$ 20,00):</Text>
          <Input
            value={currencyMaskFormat(modalData.value).value}
            onChange={(e) =>
              setModalData({
                ...modalData,
                value: currencyMaskFormat(e.target.value).raw
              })
            }
          />
        </Box>

        <Box
          bg="orange.50"
          border="1px solid"
          borderColor="orange.200"
          borderRadius="md"
          p="14px"
        >
          <Text fontWeight="semibold" color="orange.600" mb="6px">
            Como funciona
          </Text>
          <Text color="orange.700" fontSize="sm">
            Ao adicionar saldo via{" "}
            <Text as="span" fontWeight="semibold">
              C/C {title}
            </Text>
            , o valor será retirado da sua carteira no sistema{" "}
            <Link href={STORM_URL} isExternal fontWeight="semibold">
              Storm ({STORM_URL})
            </Link>{" "}
            e transferido para uso no{" "}
            <Text as="span" fontWeight="semibold">
              Powerhub
            </Text>
            .
          </Text>
        </Box>

        <Box
          border="1px solid"
          borderColor="gray.200"
          borderRadius="md"
          p="16px"
        >
          <Text fontWeight="semibold" mb="12px">
            Confirmar adição de saldo
          </Text>
          {saldoAtual !== undefined && (
            <Text fontSize="sm" color="gray.600" mb="10px">
              Você possui atualmente{" "}
              <Text as="span" fontWeight="semibold" color="orange.500">
                {currencyMaskFormat(saldoAtual).value}
              </Text>{" "}
              disponível.
            </Text>
          )}
          <Text fontSize="sm" color="gray.600" mb="10px">
            Você está prestes a adicionar mais{" "}
            <Text as="span" fontWeight="semibold" color="orange.500">
              {currencyMaskFormat(modalData.value).value}
            </Text>{" "}
            ao{" "}
            <Text as="span" fontWeight="semibold" color="orange.500">
              Powerhub
            </Text>
            .
          </Text>
          <Text fontSize="sm" color="gray.600" mb="16px">
            Ao confirmar, esse valor será removido da sua carteira no sistema{" "}
            <Link href={STORM_URL} isExternal color="orange.500">
              Storm ({STORM_URL})
            </Link>{" "}
            e adicionado à sua carteira no{" "}
            <Text as="span" fontWeight="semibold" color="orange.500">
              Power Hub
            </Text>
            .
          </Text>
          <Checkbox
            isChecked={estouCiente}
            onChange={(e) => setEstouCiente(e.target.checked)}
            colorScheme="orange"
          >
            Estou ciente
          </Checkbox>
        </Box>
      </Grid>
    </CustomModal>
  );
}

export const openModalAdicionarSaldoStorm = () =>
  makeEvent("openModalAdicionarSaldoStorm");
